import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  VisibilityOutlined as ViewIcon,
  FileDownloadOutlined as DownloadIcon,
  StarRounded as StarIcon,
  InsertDriveFileOutlined as FileIcon
} from '@mui/icons-material';
import DocumentViewer from '../Common/DocumentViewer';
import { createDocumentFieldProps } from '../../utils/documentUtils';
import { API_PATHS } from '../../utils/constants';
import { getToken } from '../../services/auth';

/**
 * Component for rendering document fields with view and download options
 */
const DocumentFieldRenderer = ({ field, label }) => {
  const [loading, setLoading] = useState(false);
  
  // New document viewer state
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState({
    url: '',
    fileName: '',
    uid: '',
    documentType: ''
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Extract document props from field
  const { hasFile, hasFileStrict, fileName, documentUrl, refs = [] } = createDocumentFieldProps(field);
  
  // Debug logging to help diagnose issues
  console.log('DocumentFieldRenderer props:', { field, hasFile, hasFileStrict, fileName, documentUrl, refsCount: Array.isArray(refs) ? refs.length : 0 });

  /**
   * Handle viewing the document using the new DocumentViewer component
   */
  // Helper to open a filePath using Authorization header and blob URL
  const openFilePathInNewTab = async (filePath, name) => {
    try {
      const token = getToken();
      const fullUrl = `${API_PATHS.V1}/files/download?filePath=${encodeURIComponent(filePath)}`;
      const res = await fetch(fullUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`Failed to open document: ${res.status} ${res.statusText}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      // Open in same window
      window.location.href = blobUrl;
    } catch (e) {
      console.error('Error opening filePath document', e);
      setSnackbar({ open: true, message: e.message || 'Error opening document', severity: 'error' });
    }
  };

  const handleViewDocument = () => {
    if (!documentUrl && (!refs || refs.length === 0)) {
      setSnackbar({
        open: true,
        message: 'No document URL available',
        severity: 'error'
      });
      return;
    }
    
    // If multiple refs exist, open the primary or first ref
    if (refs && refs.length > 0) {
      const primary = refs.find(r => r.isPrimary) || refs[0];
      const fp = primary.filePath || primary.path;
      if (fp) {
        openFilePathInNewTab(fp, primary.fileName || fileName || 'Document');
        return;
      }
    }

    // Extract uid and documentType if present in the URL
    let uid = '';
    let documentType = '';
    
    try {
      // Try to extract uid and documentType from the URL
      const urlParts = documentUrl.split('/');
      const usersIndex = urlParts.findIndex(part => part === 'users');
      
      if (usersIndex !== -1 && urlParts.length > usersIndex + 2) {
        const extractedUid = urlParts[usersIndex + 1];
        const extractedDocType = urlParts[usersIndex + 2];
        
        // Check if this is a dynamic service field (UUID pattern)
        const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(extractedDocType);
        
        if (isDynamicServiceField) {
          console.log('Dynamic service field detected in DocumentFieldRenderer, not passing uid/documentType to avoid employee document API calls');
          // Don't set uid and documentType for dynamic service fields
          // This prevents DocumentViewer from trying to use employee document APIs
        } else {
          uid = extractedUid;
          documentType = extractedDocType;
        }
      }
    } catch (e) {
      console.log('Could not extract uid and documentType from URL:', e);
    }
    
    // Set the current document and open the viewer
    setCurrentDocument({
      url: documentUrl,
      fileName: fileName || 'Document',
      uid: uid,
      documentType: documentType
    });
    
    setDocumentViewerOpen(true);
  };

  /**
   * Handle downloading the document
   */
  const handleDownloadDocument = async () => {
    if (!documentUrl && (!refs || refs.length === 0)) {
      setSnackbar({
        open: true,
        message: 'No document URL available',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // If multiple refs, download primary or first
      if (refs && refs.length > 0) {
        const primary = refs.find(r => r.isPrimary) || refs[0];
        const fp = primary.filePath || primary.path;
        if (fp) {
          const fullUrl = `${API_PATHS.V1}/files/download?filePath=${encodeURIComponent(fp)}`;
          const response = await fetch(fullUrl, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          if (!response.ok) {
            throw new Error(`Error downloading document: ${response.status} ${response.statusText}`);
          }
          const blob = await response.blob();
          const fileUrl = URL.createObjectURL(blob);
          const downloadLink = document.createElement('a');
          downloadLink.href = fileUrl;
          downloadLink.download = (primary.fileName || fileName || 'Document');
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(fileUrl);
          setSnackbar({ open: true, message: 'Document downloaded successfully', severity: 'success' });
          return;
        }
      }

      // Determine if this is a full URL or a relative path for single documentUrl
      let fullUrl;
      if (documentUrl.startsWith('http')) {
        fullUrl = documentUrl;
      } else if (documentUrl.startsWith('/v1/')) {
        fullUrl = `${API_PATHS.BASE}${documentUrl}`;
      } else if (documentUrl.startsWith('/api/')) {
        fullUrl = `${API_PATHS.BASE}${documentUrl}`;
      } else if (documentUrl.startsWith('/')) {
        fullUrl = `${API_PATHS.API}${documentUrl}`;
      } else {
        fullUrl = `${API_PATHS.API}/${documentUrl}`;
      }

      // Fetch the document
      const response = await fetch(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error downloading document: ${response.status} ${response.statusText}`);
      }

      // Get the blob data
      const blob = await response.blob();
      const fileUrl = URL.createObjectURL(blob);
      
      // Create a download link and trigger it
      const downloadLink = document.createElement('a');
      downloadLink.href = fileUrl;
      downloadLink.download = fileName || 'Document';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Clean up the object URL
      URL.revokeObjectURL(fileUrl);
      
      setSnackbar({
        open: true,
        message: 'Document downloaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error downloading document',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Download a specific reference (used by the per-file row buttons).
  const downloadRef = async (r, idx) => {
    setLoading(true);
    try {
      const token = getToken();
      const fp = r.filePath || r.path;
      if (!fp) throw new Error('No document URL available');
      const fullUrl = `${API_PATHS.V1}/files/download?filePath=${encodeURIComponent(fp)}`;
      const response = await fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error(`Error downloading document: ${response.status} ${response.statusText}`);
      const blob = await response.blob();
      const fileUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = fileUrl;
      downloadLink.download = r.fileName || fileName || `Document-${idx + 1}`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(fileUrl);
      setSnackbar({ open: true, message: 'Document downloaded successfully', severity: 'success' });
    } catch (error) {
      console.error('Error downloading document:', error);
      setSnackbar({ open: true, message: error.message || 'Error downloading document', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle closing the document viewer
   */
  const handleCloseDocumentViewer = () => {
    setDocumentViewerOpen(false);
  };

  /**
   * Handle closing the snackbar
   */
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box>
      {/* Document status and actions (the field label is rendered by the parent field wrapper) */}
      <Box display="flex" flexDirection="column">
        {(hasFileStrict ?? hasFile) ? (
          <>
            {refs && refs.length > 0 ? (
              <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
                {refs.map((r, idx) => {
                  const fp = r.filePath || r.path;
                  const name = r.fileName || fileName || `Document ${idx + 1}`;
                  return (
                    <Box
                      key={`doc-ref-${idx}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.75, borderTop: idx > 0 ? '1px solid' : 'none', borderColor: 'divider' }}
                    >
                      {r.isPrimary
                        ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                        : <FileIcon fontSize="small" color="action" />}
                      <Typography variant="body2" noWrap title={name} sx={{ flex: 1, minWidth: 0 }}>{name}</Typography>
                      <Tooltip title="View">
                        <span>
                          <IconButton size="small" color="primary" disabled={loading}
                            onClick={() => (fp ? openFilePathInNewTab(fp, name) : handleViewDocument())}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Download">
                        <span>
                          <IconButton size="small" color="primary" disabled={loading} onClick={() => downloadRef(r, idx)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: 'grey.300', borderRadius: 1, px: 1, py: 0.75 }}>
                <FileIcon fontSize="small" color="action" />
                <Typography variant="body2" noWrap title={fileName || 'Document'} sx={{ flex: 1, minWidth: 0 }}>{fileName || 'Document'}</Typography>
                <Tooltip title="View">
                  <span>
                    <IconButton size="small" color="primary" onClick={handleViewDocument} disabled={loading}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Download">
                  <span>
                    <IconButton size="small" color="primary" onClick={handleDownloadDocument} disabled={loading}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {loading && <CircularProgress size={16} sx={{ ml: 0.5 }} />}
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No document available
          </Typography>
        )}
      </Box>
      
      {/* New Document Viewer Component */}
      <DocumentViewer
        open={documentViewerOpen}
        onClose={handleCloseDocumentViewer}
        documentUrl={currentDocument?.url}
        fileName={currentDocument?.fileName}
        uid={currentDocument?.uid}
        documentType={currentDocument?.documentType}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentFieldRenderer;
